/**
 * The `clock` namespace. Its shape is the contract for every locale.
 * Interpolation: `{{name}}`, `{{date}}`, `{{dates}}`, `{{time}}`, `{{timeZone}}`, `{{count}}`, `{{value}}`.
 */
export const enUs = {
  nav: {
    clock: 'Clock',
    records: 'Records',
    requests: 'Requests',
    approvals: 'Approvals',
    team: 'Team',
    schedules: 'Schedules',
  },
  clock: {
    now: 'Current time ({{timeZone}})',
    status: 'Status',
    since: 'Since {{time}}',
    today: "Today's punches",
    noPunches: 'No punches yet today.',
    recorded: 'Recorded at {{time}}.',
    needsCorrection:
      'The session started on {{dates}} ended automatically 24 hours after it started. Submit a modification request with the actual end time.',
    requestCorrection: 'Request a correction',
    endAgainHint: 'Press End work again to update your end time.',
    location: {
      title: 'Location',
      granted: 'Location access is allowed.',
      prompt: 'Your browser will ask for your location when you punch.',
      denied: 'Location access is blocked. Allow it in your browser settings to punch.',
      unsupported: 'This browser cannot determine your location.',
      locating: 'Getting your location…',
    },
    error: {
      title: 'Your location is needed',
      denied:
        "Location access is blocked for this site. Allow location access in your browser's site settings, then try again.",
      unavailable:
        'Your location could not be determined. Make sure location services are turned on for this device, then try again.',
      timeout:
        'Getting your location took too long. Move to a place with better reception, then try again.',
      unsupported:
        'This browser cannot determine your location. Use another browser or device to punch.',
    },
  },
  records: {
    title: 'Records',
    titleOf: 'Records of {{name}}',
    previousMonth: 'Previous month',
    nextMonth: 'Next month',
    date: 'Date',
    schedule: 'Schedule',
    actual: 'Worked hours',
    breaks: 'Breaks',
    worked: 'Worked',
    late: 'Late',
    earlyLeave: 'Early leave',
    overtime: 'Overtime',
    holidayWork: 'Holiday work',
    notes: 'Notes',
    dayOff: 'Day off',
    missingPunch: 'Missing punch',
    modified: 'Modified',
    pending: 'Request pending',
    outsideGeofence: 'Outside geofence',
    endUpdated: 'End time updated',
    autoEnded: 'Ended automatically',
    totals: 'Month totals',
    calendar: 'Calendar',
    daysWorked: 'Days worked',
    lateCount: 'Late arrivals',
    earlyLeaveCount: 'Early leaves',
    missingPunches: 'Missing punches',
    exportMonth: 'Month (CSV)',
    exportYear: 'Year (CSV)',
  },
  day: {
    back: 'Back to records',
    schedule: 'Schedule',
    noSchedule: 'No schedule',
    noRecord: 'No record for this day.',
    breaks: 'Breaks',
    noBreaks: 'No breaks',
    punches: 'Punches',
    showOnMap: 'Map',
    evidence: 'Punch details for managers and admins',
    ip: 'IP address',
    accuracy: 'Accuracy',
    geofence: 'Geofence',
    nearestArea: 'Nearest area',
    distance: 'Distance',
    meters: '{{value}} m',
    requests: 'Modification requests',
    requestChange: 'Request a modification',
  },
  request: {
    title: 'Modification request',
    hint: 'Enter the times as they should be recorded, including every break. Your manager approves the change.',
    workDate: 'Work date',
    start: 'Start',
    end: 'End',
    breaks: 'Breaks',
    addBreak: 'Add break',
    removeBreak: 'Remove break',
    breakStart: 'Break start',
    breakEnd: 'Break end',
    reason: 'Reason',
    submit: 'Submit request',
  },
  requests: {
    title: 'My requests',
    empty: 'No requests yet.',
    newRequest: 'New request',
    withdraw: 'Withdraw',
    requestedAt: 'Requested {{time}}',
    reviewedBy: 'By {{name}}, {{time}}',
    comment: 'Comment',
    requested: 'Requested',
    current: 'Current record',
  },
  approvals: {
    title: 'Approvals',
    empty: 'Nothing to approve.',
    comment: 'Comment (optional)',
  },
  team: {
    title: 'Team',
    today: 'Today',
    month: 'Month',
    group: 'Group',
    allGroups: 'All groups',
    member: 'Member',
    state: 'Status',
    since: 'Started',
    warnings: 'Warnings',
    outsideGeofence: 'Punched outside the geofence',
    needsCorrection: 'A session needs correction',
    pendingRequests: '{{count}} pending',
    worked: 'Worked',
    daysWorked: 'Days',
    overtime: 'Overtime',
    late: 'Late',
    missingPunches: 'Missing',
    outsideDays: 'Outside',
    empty: 'No members.',
  },
  schedules: {
    title: 'Schedules',
    group: 'Group',
    groupDefault: 'Group default',
    individual: 'Individual schedule',
    member: 'Member',
    effectiveFrom: 'Effective from',
    weekday: 'Day',
    workday: 'Workday',
    start: 'Start',
    end: 'End',
    breakMinutes: 'Break (min)',
    overnightHint: 'An end time at or before the start time means the shift ends the next day.',
    versions: 'Versions',
    inEffect: 'In effect',
    empty: 'No schedule yet.',
    saved: 'Schedule saved.',
    useGroupDefault: 'No individual schedule: the group default applies.',
  },
  geofences: {
    title: 'Geofences',
    description:
      'Punches outside every active area of the group are accepted but flagged for managers and admins.',
    name: 'Name',
    latitude: 'Latitude',
    longitude: 'Longitude',
    radius: 'Radius (m)',
    active: 'Active',
    add: 'Add area',
    mapHint: 'Click the map to move the center.',
    empty: 'No areas for this group.',
  },
  audit: {
    title: 'Audit log',
    time: 'Time',
    actor: 'By',
    action: 'Action',
    target: 'Target',
    system: 'System',
    actions: {
      ScheduleChanged: 'Schedule changed',
      GeofenceChanged: 'Geofence changed',
      ModificationRequested: 'Modification requested',
      ModificationApproved: 'Modification approved',
      ModificationRejected: 'Modification rejected',
      ModificationCancelled: 'Modification withdrawn',
      CsvExported: 'CSV exported',
      LocalAdminGranted: 'Administrator added',
      LocalAdminRevoked: 'Administrator removed',
    },
  },
};

export type ClockResources = typeof enUs;
